import type { FC } from 'react';
import {
  Button,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CountrySelector,
  FDatePicker,
  Modal,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import {
  useGetAllCountriesPhoenix,
  useSortedCountries,
} from '@pflegenavi/service-provider-frontend/api';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { keepPreviousData } from '@tanstack/react-query';

export interface UboFormData {
  FirstName: string;
  LastName: string;
  Birthday: Date | null;
  Nationality: string;
  Address: {
    AddressLine1: string;
    City: string;
    PostalCode: string;
    Country: string;
  };
  Birthplace: {
    City: string;
    Country: string;
  };
}

interface Country {
  value: string;
  label: string;
}

interface UboFormModalViewProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: UboFormData) => void;
  countries: Country[];
}

export const UboFormModalView: FC<UboFormModalViewProps> = ({
  open,
  handleClose,
  onSubmit,
  countries,
}) => {
  const { t } = useTranslation();

  const uboValidationSchema = Yup.object().shape({
    FirstName: Yup.string().required(t('field.required.first-name')),
    LastName: Yup.string().required(t('field.required.last-name')),
    Birthday: Yup.date().nullable().required(t('field.required.birth-date')),
    Nationality: Yup.string().required(t('field.required.nationality')),
    Address: Yup.object().shape({
      AddressLine1: Yup.string().required(t('field.required.address')),
      City: Yup.string().required(t('field.required.city')),
      PostalCode: Yup.string().required(t('field.required.zip')),
      Country: Yup.string().required(t('field.required.country')),
    }),
    Birthplace: Yup.object().shape({
      City: Yup.string().required(t('field.required.birth-city')),
      Country: Yup.string().required(t('field.required.birth-country')),
    }),
  });

  const initialValues: UboFormData = {
    FirstName: '',
    LastName: '',
    Birthday: null,
    Nationality: '',
    Address: {
      AddressLine1: '',
      City: '',
      PostalCode: '',
      Country: '',
    },
    Birthplace: {
      City: '',
      Country: '',
    },
  };

  return (
    <Modal open={open} handleClose={handleClose}>
      <Card data-testid="ubo-form-modal" sx={{ p: 3, maxWidth: 800 }}>
        <Stack gap={2}>
          <Typography variant="h5">{t('kyc.ubo.add-ubo')}</Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={uboValidationSchema}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <Form>
                <Stack gap={3}>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="FirstName"
                        label={t('kyc.ubo.first-name')}
                        inputProps={{ 'data-testid': 'ubo-first-name-input' }}
                        error={
                          formik.touched.FirstName && !!formik.errors.FirstName
                        }
                        helperText={
                          formik.touched.FirstName && formik.errors.FirstName
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="LastName"
                        label={t('kyc.ubo.last-name')}
                        inputProps={{ 'data-testid': 'ubo-last-name-input' }}
                        error={
                          formik.touched.LastName && !!formik.errors.LastName
                        }
                        helperText={
                          formik.touched.LastName && formik.errors.LastName
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <FDatePicker
                        dataTestId="ubo-birthday-input"
                        name="Birthday"
                        formik={formik}
                        label={t('kyc.ubo.birthday')}
                        maxDate={new Date()}
                      />
                    </Grid>
                    <Grid size={6}>
                      <CountrySelector
                        data-testid="ubo-nationality-select"
                        label={t('kyc.ubo.nationality')}
                        onChangeCountry={(newCountryId) => {
                          formik.setFieldValue('Nationality', newCountryId);
                        }}
                        onBlur={() =>
                          formik.setFieldTouched('Nationality', true)
                        }
                        countries={countries}
                        countryId={formik.values.Nationality}
                        name="Nationality"
                        errorMessage={
                          formik.touched.Nationality &&
                          formik.errors.Nationality
                            ? String(formik.errors.Nationality)
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="h6">{t('kyc.ubo.address')}</Typography>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="Address.AddressLine1"
                        label={t('kyc.ubo.address-line')}
                        inputProps={{
                          'data-testid': 'ubo-address-line1-input',
                        }}
                        error={
                          formik.touched.Address?.AddressLine1 &&
                          !!formik.errors.Address?.AddressLine1
                        }
                        helperText={
                          formik.touched.Address?.AddressLine1 &&
                          formik.errors.Address?.AddressLine1
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="Address.City"
                        label={t('kyc.ubo.city')}
                        inputProps={{ 'data-testid': 'ubo-city-input' }}
                        error={
                          formik.touched.Address?.City &&
                          !!formik.errors.Address?.City
                        }
                        helperText={
                          formik.touched.Address?.City &&
                          formik.errors.Address?.City
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="Address.PostalCode"
                        label={t('kyc.ubo.postal-code')}
                        inputProps={{ 'data-testid': 'ubo-postal-code-input' }}
                        error={
                          formik.touched.Address?.PostalCode &&
                          !!formik.errors.Address?.PostalCode
                        }
                        helperText={
                          formik.touched.Address?.PostalCode &&
                          formik.errors.Address?.PostalCode
                        }
                      />
                    </Grid>
                    <Grid size={12}>
                      <CountrySelector
                        data-testid="ubo-country-select"
                        label={t('kyc.ubo.country')}
                        onChangeCountry={(newCountryId) => {
                          formik.setFieldValue('Address.Country', newCountryId);
                        }}
                        onBlur={() =>
                          formik.setFieldTouched('Address.Country', true)
                        }
                        countries={countries}
                        countryId={formik.values.Address.Country}
                        name="Address.Country"
                        errorMessage={
                          formik.touched.Address?.Country &&
                          formik.errors.Address?.Country
                            ? String(formik.errors.Address.Country)
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>

                  <Typography variant="h6">
                    {t('kyc.ubo.birthplace')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Field
                        as={TextField}
                        fullWidth
                        name="Birthplace.City"
                        label={t('kyc.ubo.birth-city')}
                        inputProps={{ 'data-testid': 'ubo-birth-city-input' }}
                        error={
                          formik.touched.Birthplace?.City &&
                          !!formik.errors.Birthplace?.City
                        }
                        helperText={
                          formik.touched.Birthplace?.City &&
                          formik.errors.Birthplace?.City
                        }
                      />
                    </Grid>
                    <Grid size={6}>
                      <CountrySelector
                        data-testid="ubo-birth-country-select"
                        label={t('kyc.ubo.birth-country')}
                        onChangeCountry={(newCountryId) => {
                          formik.setFieldValue(
                            'Birthplace.Country',
                            newCountryId
                          );
                        }}
                        onBlur={() =>
                          formik.setFieldTouched('Birthplace.Country', true)
                        }
                        countries={countries}
                        countryId={formik.values.Birthplace.Country}
                        name="Birthplace.Country"
                        errorMessage={
                          formik.touched.Birthplace?.Country &&
                          formik.errors.Birthplace?.Country
                            ? String(formik.errors.Birthplace.Country)
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" justifyContent="flex-end" gap={2}>
                    <Button
                      data-testid="ubo-cancel-button"
                      onClick={handleClose}
                    >
                      {t('actions.cancel')}
                    </Button>
                    <Button
                      data-testid="ubo-save-button"
                      type="submit"
                      variant="contained"
                    >
                      {t('actions.save')}
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Card>
    </Modal>
  );
};

interface UboFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: UboFormData) => void;
}

export const UboFormModal: FC<UboFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
}) => {
  const { data: countriesData, isPending: isCountriesLoading } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });

  const countries = useSortedCountries(countriesData ?? []);

  if (isCountriesLoading || !countries || countries.length === 0) {
    return null;
  }

  return (
    <UboFormModalView
      open={open}
      handleClose={handleClose}
      onSubmit={onSubmit}
      countries={countries}
    />
  );
};
